import { catalogKey, type AdminVehicleCatalogModelDto } from '@carcheck/shared';

/**
 * Client-side filter of a make's models in the drawer (AUT-437). The detail
 * carries every model of the make (Renault ≈ 280), so search and the tabs
 * run in memory. Search compares `catalogKey`s of the name and every alias
 * — the same identity the server and the app use («Mégane» ≡ «megane»).
 */
export const MODEL_TABS = ['all', 'popular', 'onSale', 'legacy', 'hidden'] as const;
export type ModelTab = (typeof MODEL_TABS)[number];

type Model = AdminVehicleCatalogModelDto;

const IN_TAB: Record<ModelTab, (m: Model) => boolean> = {
  all: () => true,
  popular: (m) => m.popular,
  onSale: (m) => m.onSale,
  legacy: (m) => m.legacy,
  hidden: (m) => m.status === 'hidden',
};

export const tabCounts = (models: readonly Model[]): Record<ModelTab, number> => {
  const counts = { all: 0, popular: 0, onSale: 0, legacy: 0, hidden: 0 };
  for (const m of models) {
    for (const tab of MODEL_TABS) if (IN_TAB[tab](m)) counts[tab] += 1;
  }
  return counts;
};

const matches = (m: Model, key: string): boolean =>
  catalogKey(m.name).includes(key) || m.aliases.some((a) => catalogKey(a.alias).includes(key));

/** Most-used first (the column «В отчётах»), then by name. */
const byUsage = (a: Model, b: Model): number =>
  b.usageCount - a.usageCount || a.name.localeCompare(b.name, 'ru');

export const filterModels = (models: readonly Model[], tab: ModelTab, query: string): Model[] => {
  const key = catalogKey(query.trim());
  return models.filter((m) => IN_TAB[tab](m) && (key === '' || matches(m, key))).sort(byUsage);
};

/** Models the app snapshot carries — a make with none is not shown in the app. */
export const activeModelCount = (models: readonly Model[]): number =>
  models.filter((m) => m.status === 'active').length;
